// Who is signed in and what they may do. Pages and buttons ask useMe().can(PERMISSION.x) or use the kit's <Can>;
// the API checks the same permission again, so hiding a button is never the only guard.
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { PermissionProvider } from '@the-kit/ui'
import type { Session, TwoStepRule, User } from '@/api/schemas'
import { userCan, type Permission } from '@/constants/permissions'
import { ROLE_LABEL } from '@/constants/roles'

interface SessionValue {
  user: User
  /** optional: each person turns 2-step on; required: everyone has it */
  twoStepRule: TwoStepRule
  can: (permission: Permission) => boolean
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ session, children }: { session: Session; children: ReactNode }) {
  const { user, two_step_rule: twoStepRule } = session
  const value = useMemo<SessionValue>(
    () => ({ user, twoStepRule, can: (permission) => userCan(user, permission) }),
    [user, twoStepRule]
  )
  return (
    <SessionContext.Provider value={value}>
      <PermissionProvider
        value={{
          can: (permission) => userCan(user, permission as Permission),
          roleLabel: ROLE_LABEL[user.role],
        }}
      >
        {children}
      </PermissionProvider>
    </SessionContext.Provider>
  )
}

export function useMe(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('SessionProvider is missing')
  return value
}
